import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { SCRAPE_CATEGORY_KEY } from "./keys";

import { ScrapedCategoryRepository } from "@/repositories/scrape-category";
import { PaginatedResponse } from "@/types";
import {
  ScrapedCategory,
  UpdateScrapedCategory,
} from "@/types/scrape-category";

export const useUpdateScrapedCategory = () => {
  const queryClient = useQueryClient();
  const scrapedCategoryRepository = useRef(new ScrapedCategoryRepository());

  return useMutation({
    mutationFn: async (data: UpdateScrapedCategory) => {
      return await scrapedCategoryRepository.current.update(data);
    },
    onMutate: async (newData: UpdateScrapedCategory) => {
      await queryClient.cancelQueries({
        predicate: (query) =>
          query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
          query.queryKey[1] === "LIST",
      });

      const previousQueries = queryClient.getQueriesData({
        predicate: (query) =>
          query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
          query.queryKey[1] === "LIST",
      });

      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === SCRAPE_CATEGORY_KEY &&
            query.queryKey[1] === "LIST",
        },
        (oldData) => {
          if (!oldData) return oldData;
          const cachedData = oldData as PaginatedResponse<ScrapedCategory>;

          return {
            ...cachedData,
            data: cachedData.data.map((category) =>
              category.id === newData.id
                ? {
                    ...category,
                    blacklist:
                      newData.blacklist !== undefined
                        ? newData.blacklist
                        : category.blacklist,
                    linkedCategoryId:
                      newData.linkedCategoryId !== undefined
                        ? newData.linkedCategoryId
                        : category.linkedCategoryId,
                  }
                : category,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
